import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import type { CropArea, MediaImage, UpdateMediaImageRequest } from "./mediaApi";
import { cropImage, deleteImage, listImages, updateImage, uploadImage } from "./mediaApi";

export const useImages = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, error } = useQuery({
    queryKey: ["media", "images"],
    queryFn: ({ signal }) => listImages(signal),
  });

  useEffect(() => {
    if (!isLoading && error) {
      console.warn("Unable to fetch images", error);
      enqueueSnackbar(`Nie udało się pobrać obrazów. Powód: ${error.message}`, {
        variant: "error",
      });
    }
  }, [isLoading, error]);

  const addImageMutation = useMutation<MediaImage, unknown, FormData>({
    mutationFn: (formData) => uploadImage(formData),
    onSuccess: async () => {
      enqueueSnackbar("Grafika została dodana", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["media", "images"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać grafiki. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const updateImageMutation = useMutation<MediaImage, unknown, { id: string; data: UpdateMediaImageRequest }>({
    mutationFn: ({ id, data }) => updateImage(id, data),
    onSuccess: async () => {
      enqueueSnackbar("Metadane grafiki zostały zaktualizowane", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["media", "images"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować metadanych grafiki. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const cropImageMutation = useMutation<MediaImage, unknown, { id: string; area: CropArea }>({
    mutationFn: ({ id, area }) => cropImage(id, { area }),
    onSuccess: async () => {
      enqueueSnackbar("Wykadrowana grafika została zapisana", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["media", "images"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się wykadrować grafiki. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const deleteImageMutation = useMutation<void, unknown, string>({
    mutationFn: (imageId) => deleteImage(imageId),
    onSuccess: async () => {
      enqueueSnackbar("Grafika została usunięta", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["media", "images"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć grafiki. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  return {
    images: data ?? [],
    addImage: (formData: FormData) => addImageMutation.mutateAsync(formData),
    updateImage: (id: string, data: UpdateMediaImageRequest) => updateImageMutation.mutateAsync({ id, data }),
    cropImage: (id: string, area: CropArea) => cropImageMutation.mutateAsync({ id, area }),
    deleteImage: (imageId: string) => deleteImageMutation.mutateAsync(imageId),
    isLoading,
  };
};
